import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { it } from './it.js';

/**
 * Minimal i18n: English strings are the keys, `it.js` maps them to Italian.
 * Missing entries fall back to the English key, so untranslated strings are
 * never broken — just English. Components import the module-level `t`;
 * the provider remounts the app subtree on language change (key={lang}),
 * so every component re-renders with the new language.
 *
 *   t('New task')                          -> 'Nuova attività'
 *   t('Page {a} of {b}', { a: 1, b: 4 })   -> 'Pagina 1 di 4'
 */

const DICTS = { it };
const LANGS = ['en', 'it'];

function detectLang() {
  try {
    const saved = localStorage.getItem('lang');
    if (LANGS.includes(saved)) return saved;
  } catch { /* private mode */ }
  return (navigator.language || '').toLowerCase().startsWith('it') ? 'it' : 'en';
}

let currentLang = detectLang();

export function getLang() { return currentLang; }

export function t(key, vars) {
  const dict = DICTS[currentLang];
  let s = (dict && dict[key]) || key;
  if (vars) {
    for (const [k, v] of Object.entries(vars)) s = s.replaceAll(`{${k}}`, String(v));
  }
  return s;
}

/* Human labels for DB enum values (statuses, priorities, company types).
   English side lives here; Italian side comes from the dictionary. */
const ENUM_LABELS = {
  // tasks
  todo: 'To do', in_progress: 'In progress', blocked: 'Blocked', review: 'Review', done: 'Done',
  // projects / fund clients
  lead: 'Lead', active: 'Active', on_hold: 'On hold', completed: 'Completed', cancelled: 'Cancelled',
  preparing: 'Preparing', submitted: 'Submitted', approved: 'Approved', rejected: 'Rejected',
  // contracts
  draft: 'Draft', sent: 'Sent', signed: 'Signed', expired: 'Expired',
  // funds
  scouting: 'Scouting', open: 'Open', reporting: 'Reporting', closed: 'Closed',
  // documents
  to_fill: 'To fill', filled: 'Filled',
  // sal
  planned: 'Planned', paid: 'Paid',
  // invoices
  partially_paid: 'Partially paid', overdue: 'Overdue', proforma: 'Proforma', final: 'Final',
  // offers
  negotiating: 'Negotiating', accepted: 'Accepted',
  // client flags
  info: 'Info', warning: 'Warning', critical: 'Critical',
  // conversations
  call: 'Call', meeting: 'Meeting', email: 'Email', whatsapp: 'WhatsApp', note: 'Note',
  // priorities
  low: 'Low', medium: 'Medium', high: 'High', urgent: 'Urgent',
  // company types
  manufacturer: 'Manufacturer', client: 'Client', own: 'Our company',
};

export function enumLabel(value) {
  if (value == null || value === '') return '—';
  return t(ENUM_LABELS[value] || value);
}

export function fmtDate(d) {
  if (!d) return '—';
  return new Date(d + 'T00:00:00').toLocaleDateString(
    currentLang === 'it' ? 'it-IT' : 'en-GB',
    { day: '2-digit', month: 'short', year: 'numeric' },
  );
}

const I18nCtx = createContext({ lang: 'en', setLang: () => {} });

export function I18nProvider({ children }) {
  const [lang, setLangState] = useState(currentLang);

  useEffect(() => {
    currentLang = lang;
    document.documentElement.lang = lang;
    try { localStorage.setItem('lang', lang); } catch { /* private mode */ }
  }, [lang]);

  const value = useMemo(() => ({ lang, setLang: setLangState }), [lang]);

  // key={lang} remounts the subtree so module-level t() stays consistent
  // everywhere without threading a hook through every component.
  return (
    <I18nCtx.Provider value={value}>
      <div style={{ display: 'contents' }} key={lang}>{children}</div>
    </I18nCtx.Provider>
  );
}

export function useI18n() {
  return useContext(I18nCtx);
}
